/*
 * -----------------------------------------------------------------------
 *  <copyright file="study-dashboard-panel.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Fade from '@mui/material/Fade';

import { useBoolean, useTranslation } from 'hooks';
import StudyDashboardItem from './study-dashboard-item';
import { PanelFields } from 'queries/generated/fragments.generated';

import './styles.scss';

interface IStudyDashboardPanel {
    panelItems: any[];
    disabled: boolean;
    title: string;
    setChildPanel?: Function;
    childPanel?: any;
    setPanelItems?: Function;
    itemsForAdding?: any;
    customKey?: string;
}

const StudyDashboardPanel = ({
    panelItems,
    disabled,
    setChildPanel,
    childPanel,
    title,
    setPanelItems,
    itemsForAdding,
    customKey,
}: IStudyDashboardPanel) => {
    const { t } = useTranslation();
    const showMenu = useBoolean(false);
    const showBorder = useBoolean(false);
    const [itemToMove, setItemToMove] = useState(null);

    const [itemsToAdd, setItemsToAdd] = useState([]);
    const [search, setSearch] = useState('');

    let timeout: any;
    let timeoutLong: any;

    useEffect(
        () => () => {
            clearTimeout(timeout);
            clearTimeout(timeoutLong);
        },
        [],
    );

    useEffect(() => {
        if (itemsForAdding) {
            const array = [...itemsForAdding].filter(
                (item: PanelFields) =>
                    item.active &&
                    panelItems.findIndex((el: PanelFields) => el.id === item.id) < 0 &&
                    item.id !== childPanel?.id,
            );
            setItemsToAdd(orderBy(array, [{ field: 'name', dir: 'asc' }]));
        }
    }, [itemsForAdding, panelItems, childPanel]);

    useEffect(() => {
        if (disabled) showMenu.setFalse();
    }, [disabled]);

    const moveItem = (oldIndex: number, newIndex: number) => {
        showBorder.setTrue();
        setItemToMove(panelItems[oldIndex]);
        timeout = setTimeout(() => {
            const array = [...panelItems];
            array[oldIndex].index = newIndex;
            array[newIndex].index = oldIndex;
            if (newIndex >= array.length) newIndex = array.length - 1;
            array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
            setPanelItems(array);
        }, 600); // For creating effect of delay by design
        timeoutLong = setTimeout(() => {
            showBorder.setFalse();
            setItemToMove(null);
        }, 1200); // For creating effect of delay by design
    };

    const removeItem = (item: any) => {
        let array = [...panelItems];
        if (array.length > 1) {
            // we cannot use item.index because the index numbers were not consistent
            // therefor we find the item by id and remove the item.
            const itemIndex: number = array.map((x) => x.id).indexOf(item.id);
            array.splice(itemIndex, 1);
        } else {
            array = [];
        }
        setPanelItems(array);
    };

    const addItem = (item: any) => {
        if (item) {
            const array = [...panelItems];
            array.splice(panelItems.length, 0, { ...item });
            const itemsToAddArray = [...itemsToAdd];
            const itemIndex: number = itemsToAddArray.map((x: any) => x.id).indexOf(item.id);
            itemsToAddArray.splice(itemIndex, 1);
            setItemsToAdd(itemsToAddArray);
            setPanelItems && setPanelItems(array);
        }
    };

    return (
        <div className="dashboard-panel">
            <div className="header-panel">
                <div className="left">
                    <div className="item-number">{panelItems.length}</div>
                    <div className="title">{title}</div>
                </div>
                <div className="right">
                    <span
                        className={`add-item ${disabled ? 'disabled' : ''}`}
                        onClick={() => {
                            if (!disabled) showMenu.setValue(!showMenu.value);
                        }}
                        data-testid="add-item"
                    >
                        <FontAwesomeIcon icon={['far', 'search-plus']} className="icon" />
                    </span>
                </div>
                <Fade in={showMenu.value} timeout={200}>
                    <div className={`dashboard-panel-menu ${showMenu.value ? 'visible' : ''}`}>
                        <Autocomplete
                            id={customKey ? `${customKey}-search` : 'dashboard-input-search'}
                            options={itemsToAdd}
                            open={!!showMenu.value}
                            noOptionsText={t('noResults')}
                            getOptionLabel={(option) => option.name || ''}
                            clearOnBlur={false}
                            value=""
                            inputValue={search}
                            onChange={(event: any, value: any) => {
                                addItem(value);
                                showMenu.setFalse();
                                setSearch('');
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    margin="normal"
                                    onChange={(event: any) => {
                                        setSearch(event.target.value);
                                    }}
                                    slotProps={{
                                        ...params.slotProps,
                                        input: { ...params.slotProps.input, disableUnderline: true },
                                    }}
                                />
                            )}
                            data-testid=""
                            disablePortal
                        />
                    </div>
                </Fade>
            </div>
            {panelItems?.length
                ? panelItems.map((item: any, index: number) => (
                      <StudyDashboardItem
                          panel={item}
                          index={index}
                          first={index === 0}
                          last={index === panelItems.length - 1}
                          selected={childPanel ? childPanel?.id === item.id : false}
                          setChildPanel={setChildPanel}
                          disabled={disabled}
                          moveItem={moveItem}
                          removeItem={removeItem}
                          hasChild={!!item.childPanels}
                          showBorder={showBorder.value && itemToMove?.id === item.id}
                      />
                  ))
                : null}
        </div>
    );
};

export default StudyDashboardPanel;
